import { ENTRA_TOKEN_KEY, LOCAL_ROLE_TOKEN_KEY, LOCAL_SESSION_TOKEN_KEY } from '../auth/constants';
import { deviceSignalsHeader } from '../utils/deviceSignals';
import { markActivity, notifySessionEnd } from '../auth/session';

const API = process.env.REACT_APP_API_URL || '';

/** True when the caller holds an actual credential rather than a dev role name. */
function hasRealSession() {
  return !!(localStorage.getItem(ENTRA_TOKEN_KEY) || localStorage.getItem(LOCAL_SESSION_TOKEN_KEY));
}

function getAuthHeader(role) {
  const entraToken = localStorage.getItem(ENTRA_TOKEN_KEY);
  if (entraToken) return `Bearer ${entraToken}`;
  // A password session is a real credential and outranks the dev role token, which is only a
  // role name in Bearer clothing.
  const localSession = localStorage.getItem(LOCAL_SESSION_TOKEN_KEY);
  if (localSession) return `Bearer ${localSession}`;
  const roleToken = localStorage.getItem(LOCAL_ROLE_TOKEN_KEY) || role || 'Legal Analyst';
  return `Bearer ${roleToken}`;
}

function headers(role) {
  const h = { 'Content-Type': 'application/json' };
  if (role && !hasRealSession()) h['x-app-role'] = role;
  h['Authorization'] = getAuthHeader(role);
  // Environment signals for the VM policy check (server/middleware/vmGuard.js).
  const env = deviceSignalsHeader();
  if (env) h['x-client-env'] = env;
  return h;
}

/**
 * Every API call in this module goes through here.
 *
 * Two session concerns live in one place: a request counts as user activity (so the idle
 * timer does not fire while a long job is being polled), and a 401 ends the session. The
 * server verifies the token's `exp`, so a 401 is its final word — without this, an expired
 * token left a signed-in UI that could not load anything, which is what "it looks broken"
 * actually was.
 */
async function apiFetch(url, init) {
  markActivity();
  const res = await fetch(url, init);
  if (res.status === 401) await endSessionIfTokenRejected(res);
  return res;
}

/**
 * End the session only when the AUTH LAYER rejected the token.
 *
 * This used to sign the user out on any 401 at all, which meant a route refusing one
 * operation took the whole session with it: the e-imza check returned 401 for a failed
 * signature, so plugging in a card and pressing Vatandaş UYAP threw the user back to the
 * login screen with their session intact but discarded.
 *
 * The auth middleware now tags its own rejections with code SESSION_INVALID. Anything else
 * returning 401 is answered normally and the caller decides what to show.
 *
 * The body is read from a CLONE. Reading it here would consume the stream and every caller
 * downstream would find an empty response.
 */
async function endSessionIfTokenRejected(res) {
  try {
    const body = await res.clone().json();
    // POSITIVE match. The first attempt at this ended the session whenever `code` was
    // absent, which still logged people out: the e-imza gate answered 401 with
    // `eimzaRequired` and no code, so it fell straight through. The auth middleware always
    // tags its own rejections now, so a JSON 401 without that tag is a route refusing one
    // operation - never a dead session.
    if (body?.code === 'SESSION_INVALID') notifySessionEnd('unauthorized');
    return;
  } catch {
    // Not JSON at all: a proxy or the platform's own auth layer, which does mean the
    // session is gone. Reaching this through one of our own routes is not possible - they
    // all answer with JSON.
    notifySessionEnd('unauthorized');
  }
}

export async function uploadDocument(file, name, role) {
  const form = new FormData();
  form.append('file', file);
  if (name) form.append('name', name);
  const h = {};
  h['Authorization'] = getAuthHeader(role);
  if (role && !hasRealSession()) h['x-app-role'] = role;
  const res = await apiFetch(`${API}/api/documents/upload`, {
    method: 'POST',
    headers: h,
    body: form,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/**
 * Poll an AI job until it finishes. The deep drafting/analysis pipelines run several
 * reasoning passes server-side, so the POST only returns a jobId and progress arrives here.
 * `onProgress({ step, label, progress })` is called on every tick that moved.
 */
// 20 min ceiling: a full deep draft (plan → 5 section batches → review → notes) on the
// reasoning model runs for several minutes and must not be cut off by the client.
async function pollJob(jobUrl, role, onProgress, { intervalMs = 2000, timeoutMs = 1200000 } = {}) {
  const startedAt = Date.now();
  let lastLabel = '';
  for (;;) {
    if (Date.now() - startedAt > timeoutMs) throw new Error('İşlem zaman aşımına uğradı');
    const res = await apiFetch(jobUrl, { headers: headers(role) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      // A 404 here almost always means the server restarted mid-job (every deploy does it),
      // not that the id was wrong. The old behaviour surfaced the raw English string
      // "job not found or expired" to a Turkish user with no way forward, after several
      // minutes of reasoning-model work. Say what happened and that it can be re-run.
      if (res.status === 404) {
        const err = new Error('Sunucu yeniden başlatıldığı için işlem yarıda kaldı. Lütfen tekrar başlatın.');
        err.interrupted = true;
        throw err;
      }
      throw new Error(data.error || res.statusText);
    }
    if (data.label && data.label !== lastLabel) {
      lastLabel = data.label;
      onProgress?.({ step: data.step, label: data.label, progress: data.progress });
    } else if (typeof data.progress === 'number') {
      onProgress?.({ step: data.step, label: data.label || lastLabel, progress: data.progress });
    }
    if (data.status === 'done') return data.result || {};
    if (data.status === 'error') {
      const err = new Error(data.error || 'İşlem başarısız');
      // Distinguishes "the server died under it" from "the analysis itself failed", so the
      // UI can offer a retry for the first and not pretend the document is at fault.
      err.interrupted = !!data.interrupted;
      throw err;
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

export async function analyzeContract(documentId, options, role, onProgress) {
  const res = await apiFetch(`${API}/api/analysis/contract`, {
    method: 'POST',
    headers: headers(role),
    body: JSON.stringify({ documentId, options: options || {} }),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  if (data.jobId) return pollJob(`${API}/api/analysis/job/${data.jobId}`, role, onProgress);
  return data;
}

export async function getLaws(role) {
  const res = await apiFetch(`${API}/api/laws`, { headers: headers(role) });
  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error((text && text.slice(0, 240)) || `HTTP ${res.status}`);
  }
  if (!res.ok) {
    const msg = data.message || data.error;
    throw new Error(typeof msg === 'string' ? msg : text.slice(0, 240) || `HTTP ${res.status}`);
  }
  return data;
}

export async function legislationDiff(lawId, oldVersionId, newVersionId, role) {
  const res = await apiFetch(`${API}/api/diff/legislation`, {
    method: 'POST',
    headers: headers(role),
    body: JSON.stringify({ lawId, oldVersionId, newVersionId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || res.statusText);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function exportRiskHaritasi(analysisId, documentId, format, role, locale = 'tr') {
  const res = await apiFetch(`${API}/api/export`, {
    method: 'POST',
    headers: headers(role),
    body: JSON.stringify({
      type: 'risk_haritasi',
      documentId,
      analysisId,
      format,
      locale: locale === 'en' ? 'en' : 'tr',
    }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.blob();
}

export async function exportLegislationDiff(diffId, format, role) {
  const res = await apiFetch(`${API}/api/export`, {
    method: 'POST',
    headers: headers(role),
    body: JSON.stringify({
      type: 'legislation_diff',
      diffId,
      format,
    }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.blob();
}

export async function getAudit(query, role) {
  // Drop empty filters so the server doesn't reject e.g. from=''
  const clean = Object.fromEntries(Object.entries(query || {}).filter(([, v]) => v !== '' && v != null));
  const params = new URLSearchParams(clean).toString();
  const res = await apiFetch(`${API}/api/audit?${params}`, { headers: headers(role) });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/** Admin only: users present in the audit trail, for the "whose activity?" filter. */
export async function getAuditUsers(role) {
  const res = await apiFetch(`${API}/api/audit/users`, { headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data.users || [];
}

/** Download the trail as CSV or JSON, honouring the same filters as the table. */
export async function exportAudit(query, format, role) {
  const clean = Object.fromEntries(Object.entries(query || {}).filter(([, v]) => v !== '' && v != null));
  const params = new URLSearchParams({ ...clean, format });
  const res = await apiFetch(`${API}/api/audit/export?${params}`, { headers: headers(role) });
  if (!res.ok) throw new Error(await res.text());
  return res.blob();
}

/**
 * Call Case Law search API. On network failure (no response), throws an Error with isNetworkError: true
 * so the UI can show "API unreachable" only when the backend was never reached, not when the backend
 * returned an error (e.g. 502 with message containing "failed").
 */
export async function searchCaseLaw(body, role) {
  let res;
  try {
    res = await apiFetch(`${API}/api/caselaw/search`, {
      method: 'POST',
      headers: headers(role),
      body: JSON.stringify(body || {}),
    });
  } catch (e) {
    const netErr = new Error(e.message || 'Network error');
    netErr.isNetworkError = true;
    throw netErr;
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.message || res.statusText);
  return data;
}

/** Cross-index keyword search: case law + legislation + ministry decisions. */
export async function unifiedSearch(q, role, { limit = 15, sources, from, to } = {}) {
  const params = new URLSearchParams();
  params.set('q', q || '');
  if (limit) params.set('limit', String(limit));
  if (sources?.length) params.set('sources', sources.join(','));
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const res = await apiFetch(`${API}/api/search/all?${params}`, { headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

/** Ministry-decisions search (dedicated index). */
export async function searchBakanlik(q, role, { bakanlik, mevzuatTuru, kararNo, fromDate, toDate, limit = 30 } = {}) {
  const params = new URLSearchParams();
  params.set('q', q || '');
  if (bakanlik) params.set('bakanlik', bakanlik);
  if (mevzuatTuru) params.set('mevzuatTuru', mevzuatTuru);
  if (kararNo) params.set('kararNo', kararNo);
  // The index stores Resmî Gazete dates as ISO strings, so a plain range filter works.
  if (fromDate) params.set('from', fromDate);
  if (toDate) params.set('to', toDate);
  if (limit) params.set('limit', String(limit));
  const res = await apiFetch(`${API}/api/bakanlik/search?${params}`, { headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

export async function listBakanlik(role) {
  const res = await apiFetch(`${API}/api/bakanlik/list`, { headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

/**
 * `/api/config/retention` is mounted after the auth middleware, so it needs a token like
 * any other call. Sending none returned 401, and apiFetch treats a 401 as the end of the
 * session — which signed the user out the moment the Settings page mounted.
 */
export async function getRetentionConfig(role) {
  const res = await apiFetch(`${API}/api/config/retention`, { headers: headers(role) });
  if (!res.ok) throw new Error('Failed to load retention config');
  return res.json();
}

export async function runRetentionPolicy(role) {
  const res = await apiFetch(`${API}/api/admin/retention-run`, {
    method: 'POST',
    headers: headers(role),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || res.statusText);
  return data;
}

export async function searchMevzuat(q, role, limit = 100) {
  const params = new URLSearchParams({ q: q || '*', limit: String(Math.min(limit, 1000)) });
  const res = await apiFetch(`${API}/api/mevzuat/search?${params}`, { headers: headers(role) });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getMevzuatArticles(kanunNo, role) {
  const res = await apiFetch(`${API}/api/mevzuat/articles/${encodeURIComponent(kanunNo)}`, { headers: headers(role) });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function liveLegislationDiff(oldKanunNo, newKanunNo, role) {
  const res = await apiFetch(`${API}/api/diff/legislation-live`, {
    method: 'POST',
    headers: headers(role),
    body: JSON.stringify({ oldKanunNo, newKanunNo }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || data.error || res.statusText);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function customLegislationDiff(oldDocumentId, newDocumentId, role) {
  const res = await apiFetch(`${API}/api/diff/legislation-custom`, {
    method: 'POST',
    headers: headers(role),
    body: JSON.stringify({ oldDocumentId, newDocumentId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || res.statusText);
  return data;
}

export async function mixedLegislationDiff(kanunNo, documentId, side, role) {
  const res = await apiFetch(`${API}/api/diff/legislation-mixed`, {
    method: 'POST',
    headers: headers(role),
    body: JSON.stringify({ kanunNo, documentId, side }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || res.statusText);
  return data;
}

export async function summarizeCaseLaw(text, query, role) {
  const res = await apiFetch(`${API}/api/caselaw/summarize`, {
    method: 'POST',
    headers: headers(role),
    body: JSON.stringify({ text, query }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || res.statusText);
  return data;
}

export async function compareCaseLaw(decision1, decision2, role) {
  const res = await apiFetch(`${API}/api/caselaw/compare`, {
    method: 'POST',
    headers: headers(role),
    body: JSON.stringify({ decision1, decision2 }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || res.statusText);
  return data;
}

export async function getLegislationRefs(text, role) {
  const res = await apiFetch(`${API}/api/caselaw/legislation-refs`, {
    method: 'POST',
    headers: headers(role),
    body: JSON.stringify({ text }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

export async function getGeneratorTemplates(role) {
  const res = await apiFetch(`${API}/api/generator/templates`, {
    headers: role ? headers(role) : { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function searchLegalForms(q, role) {
  const params = new URLSearchParams({ q: q || '' });
  const res = await apiFetch(`${API}/api/generator/templates/search?${params}`, { headers: headers(role) });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getTemplateFields(templateId, role) {
  const res = await apiFetch(`${API}/api/generator/templates/${encodeURIComponent(templateId)}/fields`, { headers: headers(role) });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function generateDocument(payload, role) {
  const res = await apiFetch(`${API}/api/generator/generate`, {
    method: 'POST',
    headers: headers(role),
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

// ── Drafting Studio ───────────────────────────────────────────────────────────
export async function listBelgeTuruleri(role) {
  const res = await apiFetch(`${API}/api/drafting/belge-turleri`, { headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

/** Deployments the studio may draft with (model picker). */
export async function listDraftingModels(role) {
  const res = await apiFetch(`${API}/api/drafting/models`, { headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data.models || [];
}

export async function createDraft(intake, role, onProgress) {
  const res = await apiFetch(`${API}/api/drafting/draft`, {
    method: 'POST', headers: headers(role), body: JSON.stringify(intake),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  if (data.jobId) return pollJob(`${API}/api/drafting/job/${data.jobId}`, role, onProgress);
  return data;
}

export async function editDraftBlock(document, blockId, instruction, role, modelTier) {
  const res = await apiFetch(`${API}/api/drafting/edit`, {
    method: 'POST', headers: headers(role), body: JSON.stringify({ document, blockId, instruction, modelTier }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

export async function reviseDraft(document, instruction, role, modelTier, onProgress) {
  const res = await apiFetch(`${API}/api/drafting/revise`, {
    method: 'POST', headers: headers(role), body: JSON.stringify({ document, instruction, modelTier }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  if (data.jobId) return pollJob(`${API}/api/drafting/job/${data.jobId}`, role, onProgress);
  return data;
}

export async function askDraft(document, question, role, modelTier) {
  const res = await apiFetch(`${API}/api/drafting/ask`, {
    method: 'POST', headers: headers(role), body: JSON.stringify({ document, question, modelTier }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

export async function exportDraft(doc, format, role, emailOptions, fillStyle = 'marker') {
  const res = await apiFetch(`${API}/api/drafting/export`, {
    method: 'POST',
    headers: headers(role),
    body: JSON.stringify({ document: doc, format, emailOptions, fillStyle }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

/**
 * Compare 2..5 uploaded documents against one baseline.
 * `documentIds` includes the baseline; `baselineId` defaults to the first id.
 */
export async function compareDocuments(documentIds, baselineId, role, effort) {
  const res = await apiFetch(`${API}/api/diff/documents`, {
    method: 'POST', headers: headers(role), body: JSON.stringify({ documentIds, baselineId, effort }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

// ── Biçim dönüştürücü ─────────────────────────────────────────────────────────
export async function getConvertFormats(role) {
  const res = await apiFetch(`${API}/api/convert/formats`, { headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

export async function convertDocument(file, target, role) {
  const form = new FormData();
  form.append('file', file);
  form.append('target', target);
  // Multipart: let the browser set Content-Type so the boundary is correct.
  const h = { Authorization: getAuthHeader(role) };
  if (role && !hasRealSession()) h['x-app-role'] = role;
  const res = await apiFetch(`${API}/api/convert`, { method: 'POST', headers: h, body: form });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

// ── Hesaplamalar ──────────────────────────────────────────────────────────────
export async function listHesaplamalar(role) {
  const res = await apiFetch(`${API}/api/hesaplama`, { headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

async function postHesaplama(kind, payload, role) {
  const res = await apiFetch(`${API}/api/hesaplama/${kind}`, {
    method: 'POST', headers: headers(role), body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}
export const hesaplaFaiz = (payload, role) => postHesaplama('faiz', payload, role);
export const hesaplaVekalet = (payload, role) => postHesaplama('vekalet', payload, role);
export const hesaplaHarc = (payload, role) => postHesaplama('harc', payload, role);
export const hesaplaInfaz = (payload, role) => postHesaplama('infaz', payload, role);
export const hesaplaTazminat = (payload, role) => postHesaplama('tazminat', payload, role);
export const hesaplaDamga = (payload, role) => postHesaplama('damga', payload, role);
export const hesaplaKdv = (payload, role) => postHesaplama('kdv', payload, role);
export const hesaplaKira = (payload, role) => postHesaplama('kira', payload, role);
export const hesaplaNafaka = (payload, role) => postHesaplama('nafaka', payload, role);
export const hesaplaGecikme = (payload, role) => postHesaplama('gecikme', payload, role);

export async function transcribeAudio(audioBlob, lang, role) {
  const form = new FormData();
  form.append('audio', audioBlob, 'recording.wav');
  if (lang) form.append('lang', lang);
  const h = {};
  h['Authorization'] = getAuthHeader(role);
  if (role && !hasRealSession()) h['x-app-role'] = role;
  const res = await apiFetch(`${API}/api/chat/stt`, { method: 'POST', headers: h, body: form });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

export async function speakText(text, lang, role) {
  const res = await apiFetch(`${API}/api/chat/tts`, {
    method: 'POST',
    headers: headers(role),
    body: JSON.stringify({ text, lang }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.blob();
}

export async function exportChatMessage(text, format, role) {
  const res = await apiFetch(`${API}/api/chat/export`, {
    method: 'POST',
    headers: headers(role),
    body: JSON.stringify({ text, format }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.blob();
}

export async function getUsageReport({ from, to, userId } = {}, role) {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (userId) params.set('userId', userId);
  const res = await apiFetch(`${API}/api/admin/usage-report?${params}`, { headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

/**
 * The full text of a cited decision, read back from the official portal.
 *
 * Proxied through our server rather than fetched from the browser: the portals answer with
 * JSON and no CORS headers, so a direct call would be blocked, and the response would be raw
 * JSON rather than something readable.
 */
export async function getSourceDocument(role, portal, id) {
  const res = await apiFetch(
    `${API}/api/caselaw/document?source=${encodeURIComponent(portal)}&id=${encodeURIComponent(id)}`,
    { headers: headers(role) },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

/** Mahkeme türü options the corpus actually contains, with decision counts. */
export async function getCourtTypes(role) {
  const res = await apiFetch(`${API}/api/caselaw/court-types`, { headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

/** Daire values present in the corpus, optionally scoped to one court type. */
export async function getDaireler(role, courtType) {
  const q = courtType && courtType !== 'all' ? `?courtType=${encodeURIComponent(courtType)}` : '';
  const res = await apiFetch(`${API}/api/caselaw/daireler${q}`, { headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

// Local account management (Admin only)

export async function listLocalUsers(role) {
  const res = await apiFetch(`${API}/api/admin/local-users/users`, { headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

/** Returns the one-time password. It cannot be retrieved again. */
export async function createLocalUser(role, payload) {
  const res = await apiFetch(`${API}/api/admin/local-users/users`, {
    method: 'POST', headers: headers(role), body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

export async function resetLocalUserPassword(role, email) {
  const res = await apiFetch(`${API}/api/admin/local-users/users/${encodeURIComponent(email)}/reset-password`, {
    method: 'POST', headers: headers(role),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

export async function deleteLocalUser(role, email) {
  const res = await apiFetch(`${API}/api/admin/local-users/users/${encodeURIComponent(email)}`, {
    method: 'DELETE', headers: headers(role),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

export async function setLocalUserActive(role, email, active) {
  const res = await apiFetch(`${API}/api/admin/local-users/users/${encodeURIComponent(email)}/active`, {
    method: 'POST', headers: headers(role), body: JSON.stringify({ active }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

// ── Local (password) sign-in ──────────────────────────────────────────────────

/** Which sign-in methods this deployment offers. Public - called before any session exists. */
export async function getAuthMethods() {
  const res = await apiFetch(`${API}/api/auth/methods`);
  if (!res.ok) return { entra: true, local: false };
  return res.json();
}

export async function localLogin(email, password, remember = false) {
  const res = await apiFetch(`${API}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, remember: remember === true }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

/**
 * Ask for a sign-in link by email.
 *
 * The reply is deliberately the same whether or not the address has an account, so there is
 * nothing here to branch on - show the message and stop.
 */
export async function requestEmailLink(email) {
  const res = await apiFetch(`${API}/api/auth/email-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

/** Spend a link token, yielding the same session payload as a password sign-in. */
export async function spendEmailLink(token) {
  const res = await apiFetch(`${API}/api/auth/email-link/spend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

/**
 * Choose a password using a session that came from an emailed link.
 *
 * Takes the session token explicitly rather than reading storage: at this point in the
 * sign-in the token exists but has not been stored yet, because storing it navigates.
 */
/**
 * Change a user's role, and their firm alongside it.
 *
 * firmId travels with the role because Firm Admin scopes by it - promoting someone without
 * one leaves a manager who can see nobody.
 */
export async function adminSetUserRole(actorRole, email, role, firmId) {
  const res = await apiFetch(`${API}/api/admin/local-users/users/${encodeURIComponent(email)}/role`, {
    method: 'PATCH',
    headers: headers(actorRole),
    body: JSON.stringify(firmId === undefined ? { role } : { role, firmId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

/** Mail a sign-in link to one user (Admin). */
export async function adminSendLoginLink(role, email) {
  const res = await apiFetch(`${API}/api/admin/local-users/users/${encodeURIComponent(email)}/send-link`, {
    method: 'POST',
    headers: headers(role),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

/** Mail a sign-in link to every active user (Admin). Requires an explicit confirmation. */
export async function adminSendLoginLinksToAll(role) {
  const res = await apiFetch(`${API}/api/admin/local-users/users/send-links`, {
    method: 'POST',
    headers: headers(role),
    body: JSON.stringify({ confirm: true }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

export async function setPasswordWithLinkSession(sessionToken, newPassword) {
  const res = await apiFetch(`${API}/api/auth/set-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionToken}` },
    body: JSON.stringify({ newPassword }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

export async function changeLocalPassword(email, currentPassword, newPassword) {
  const res = await apiFetch(`${API}/api/auth/change-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, currentPassword, newPassword }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

// ── Token budget & spend ──────────────────────────────────────────────────────

/** The signed-in user's own monthly budget (estimate, from token counts). */
export async function getMyBudget(role) {
  const res = await apiFetch(`${API}/api/usage/me`, { headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

/** Every user's estimated usage this period (Admin / Stats Admin). */
export async function getUsageByUser(role, period) {
  const q = period ? `?period=${encodeURIComponent(period)}` : '';
  const res = await apiFetch(`${API}/api/usage/admin/users${q}`, { headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

/** Real Azure spend (Admin only). `force` bypasses the cache; use sparingly, it rate-limits. */
export async function getAzureSpend(role, force) {
  const res = await apiFetch(`${API}/api/usage/admin/spend${force ? '?force=1' : ''}`, { headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

// ── Source folders ────────────────────────────────────────────────────────────
// Named sets of material the AI can be pointed at deliberately, instead of grounding
// being all-or-nothing. See server/services/sourceFolders.js.

export async function listSourceFolders(role) {
  const res = await apiFetch(`${API}/api/folders`, { headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

export async function createSourceFolder(payload, role) {
  const res = await apiFetch(`${API}/api/folders`, {
    method: 'POST', headers: headers(role), body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data.folder;
}

export async function updateSourceFolder(id, payload, role) {
  const res = await apiFetch(`${API}/api/folders/${id}`, {
    method: 'PUT', headers: headers(role), body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data.folder;
}

export async function deleteSourceFolder(id, role) {
  const res = await apiFetch(`${API}/api/folders/${id}`, { method: 'DELETE', headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return true;
}

/** What a folder actually contributes right now, including items that no longer resolve. */
export async function resolveSourceFolder(id, role) {
  const res = await apiFetch(`${API}/api/folders/${id}/resolve`, { headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

/** Generate the AI preparation note for a case's upcoming hearings now. */
export async function briefCaseHearings(caseId, days, role) {
  const res = await apiFetch(`${API}/api/uyap/cases/${caseId}/brief`, {
    method: 'POST', headers: headers(role), body: JSON.stringify({ days }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

/** Export the suggested wording from Doküman Analizi as Word (or PDF). */
export async function exportSuggestedWording(text, title, format, role) {
  const res = await apiFetch(`${API}/api/analysis/suggested-wording/export`, {
    method: 'POST',
    headers: headers(role),
    body: JSON.stringify({ text, title, format }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || res.statusText);
  }
  return res.blob();
}

/**
 * Read the chat route's server-sent events. Calls handlers.onDelta(text) as the answer is
 * written, handlers.onStatus({ phase | tool }) while tools run, handlers.onReset() when text
 * already shown is withdrawn, and resolves with the final payload - the same object the
 * plain JSON response has always been.
 */
async function readChatStream(res, handlers) {
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let final = null;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let cut;
    while ((cut = buffer.indexOf('\n\n')) >= 0) {
      const block = buffer.slice(0, cut);
      buffer = buffer.slice(cut + 2);
      const event = (/^event: (.+)$/m.exec(block) || [])[1];
      const raw = (/^data: (.*)$/m.exec(block) || [])[1];
      if (!event || raw === undefined) continue; // ": ping" keep-alives
      let data;
      try { data = JSON.parse(raw); } catch { continue; }
      if (event === 'delta') handlers.onDelta?.(data.t || '');
      else if (event === 'status') handlers.onStatus?.(data);
      else if (event === 'reset') handlers.onReset?.();
      else if (event === 'done') final = data;
    }
  }
  if (!final) throw new Error('The answer stream ended early.');
  if (final.status >= 400) throw new Error(final.error || 'Chat request failed');
  return final;
}

// `resent` = { name, text } re-sends a previous attachment's extracted text without the file.
// `handlers` (optional) switches the reply to streaming - see readChatStream.
export async function sendChat(message, history, role, file, grounded = false, talent = '', folderIds = [], effort = 'thinking', model = '', resent = null, handlers = null) {
  const streamed = async (res) => {
    if (handlers && (res.headers.get('content-type') || '').includes('text/event-stream')) {
      return readChatStream(res, handlers);
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || res.statusText);
    return data;
  };
  if (file) {
    const form = new FormData();
    form.append('message', message || '');
    form.append('history', JSON.stringify(history || []));
    if (grounded) form.append('grounded', 'true');
    // A talent the user picked explicitly; the server validates it against the real tool
    // registry and forces it for the first round.
    if (talent) form.append('talent', talent);
    if (folderIds?.length) form.append('folderIds', JSON.stringify(folderIds));
    if (effort) form.append('effort', effort);
    if (model) form.append('model', model);
    if (handlers) form.append('stream', 'true');
    form.append('file', file);
    const h = {};
    h['Authorization'] = getAuthHeader(role);
    if (role && !hasRealSession()) h['x-app-role'] = role;
    const res = await apiFetch(`${API}/api/chat`, { method: 'POST', headers: h, body: form });
    return streamed(res);
  }
  const res = await apiFetch(`${API}/api/chat`, {
    method: 'POST',
    headers: headers(role),
    body: JSON.stringify({
      message, history: history || [], grounded, talent: talent || undefined,
      folderIds: folderIds?.length ? folderIds : undefined, effort, model: model || undefined,
      ...(resent?.text ? { attachmentName: resent.name, attachmentText: resent.text } : {}),
      ...(handlers ? { stream: true } : {}),
    }),
  });
  return streamed(res);
}

// ── UYAP case files ─────────────────────────────────────────────────────────
const EIMZA_GRANT_KEY = 'bagent-eimza-grant';
export function getEimzaGrant() { try { return localStorage.getItem(EIMZA_GRANT_KEY) || ''; } catch { return ''; } }
export function setEimzaGrant(token) { try { token ? localStorage.setItem(EIMZA_GRANT_KEY, token) : localStorage.removeItem(EIMZA_GRANT_KEY); } catch { /* ignore */ } }

// Bridge for "use this case document in the Document Generator": CaseFiles stashes the
// selected doc refs here, DraftingStudio reads & clears them on mount to preselect grounding.
const DRAFT_PRESELECT_KEY = 'bagent-draft-preselect-docs';
export function setDraftPreselectDocs(docs) {
  try { sessionStorage.setItem(DRAFT_PRESELECT_KEY, JSON.stringify(docs || [])); } catch { /* ignore */ }
}
export function takeDraftPreselectDocs() {
  try {
    const raw = sessionStorage.getItem(DRAFT_PRESELECT_KEY);
    if (!raw) return [];
    sessionStorage.removeItem(DRAFT_PRESELECT_KEY);
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function uyapHeaders(role, withGrant = false) {
  const h = headers(role);
  if (withGrant) h['x-eimza-grant'] = getEimzaGrant();
  return h;
}

/**
 * Listeners for "the e-imza grant is gone".
 *
 * Grants live in a server-side in-memory Map with a 30-minute TTL, so EVERY container
 * restart invalidates every outstanding grant while the browser happily keeps its copy in
 * localStorage. The page then renders as though verified, each call answers 403, and the
 * user sits looking at "E-imza/mobil imza doğrulaması gerekli." beside an empty case list
 * with nothing to click - the one screen that explains the situation is the gate they were
 * already past.
 */
const eimzaLostListeners = new Set();

/** Subscribe to grant loss. Returns an unsubscribe function, for use in a useEffect. */
export function onEimzaGrantLost(fn) {
  eimzaLostListeners.add(fn);
  return () => eimzaLostListeners.delete(fn);
}

async function uyapJson(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || res.statusText);
    err.status = res.status;
    err.eimzaRequired = !!data.eimzaRequired;
    err.data = data;
    // Handled HERE rather than in each caller. Every UYAP request funnels through this
    // function, so one check covers all twelve guarded routes - including the citizen case
    // list, which had no such handling and was where this surfaced.
    if (err.eimzaRequired) {
      setEimzaGrant('');
      eimzaLostListeners.forEach((fn) => { try { fn(); } catch { /* a listener must not eat the error */ } });
    }
    throw err;
  }
  return data;
}

/**
 * Start card verification by NAVIGATING the browser.
 *
 * Not a fetch. A client certificate is chosen during the TLS handshake, so an XHR issued
 * from an already-established connection can never produce a picker - the first version of
 * this asked over fetch and got "no certificate" every time. Instead: take a one-time
 * ticket over an authenticated request, then hand the browser to the public endpoint that
 * wants the certificate. It redirects back with the grant in the URL fragment.
 */
export async function startCardVerification(role) {
  const res = await apiFetch(`${API}/api/uyap/eimza/card-ticket`, {
    method: 'POST',
    headers: headers(role),
  });
  const data = await uyapJson(res);
  // Leaves the SPA on purpose; the caller does not resume after this.
  window.location.assign(data.url);
  return data;
}

/**
 * Pick up the outcome of a card verification from the URL fragment, if there is one.
 *
 * The fragment is used rather than a query string because a fragment is never sent to a
 * server: the grant stays out of the App Service HTTP logs and out of any Referer. Cleared
 * from the address bar immediately, so a shared URL carries nothing.
 *
 * @returns {{grant?:string, subject?:string, error?:string}|null}
 */
export function takeCardVerificationResult() {
  const hash = String(window.location.hash || '').replace(/^#/, '');
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  const grant = params.get('eimzaGrant');
  const error = params.get('eimzaError');
  if (!grant && !error) return null;

  params.delete('eimzaGrant');
  params.delete('eimzaSubject');
  params.delete('eimzaError');
  const rest = params.toString();
  window.history.replaceState({}, '', window.location.pathname + window.location.search + (rest ? `#${rest}` : ''));

  if (grant) {
    setEimzaGrant(grant);
    return { grant, subject: params.get('eimzaSubject') || '' };
  }
  return { error };
}

export async function eimzaStatus(role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/eimza/status`, { headers: uyapHeaders(role) }));
}
/**
 * Ask the server for something for the card to sign.
 *
 * The content has to come from the server. UYAP's own portal signs a fixed string - the same
 * bytes for every user on every login - so one captured signature is reusable forever. Ours
 * carries a nonce, names the user, and dies in five minutes.
 */
export async function eimzaCardChallenge(role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/eimza/card-challenge`, {
    method: 'POST', headers: uyapHeaders(role),
  }));
}

/**
 * Hand back what the card signed. On success the grant is stored like any other.
 *
 * The PIN is NOT in this payload and never reaches our server - it goes only to the signing
 * application on the user's own machine.
 */
export async function eimzaCardSign({ challengeToken, signedData, certificate }, role) {
  const data = await uyapJson(await apiFetch(`${API}/api/uyap/eimza/card-sign`, {
    method: 'POST',
    headers: uyapHeaders(role),
    body: JSON.stringify({ challengeToken, signedData, certificate }),
  }));
  if (data.grant) setEimzaGrant(data.grant);
  return data;
}

export async function eimzaVerify(payload, role) {
  const data = await uyapJson(await apiFetch(`${API}/api/uyap/eimza/verify`, { method: 'POST', headers: uyapHeaders(role), body: JSON.stringify(payload) }));
  if (data.grant) setEimzaGrant(data.grant);
  return data;
}

// ── UYAP portal connection (Avukat / Kurum / Vatandaş) ─────────────────────────
export async function uyapConnectionStatus(role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/connection`, { headers: uyapHeaders(role) }));
}
export async function connectUyap(portalType, credentials, role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/connect`, {
    method: 'POST', headers: uyapHeaders(role, true), body: JSON.stringify({ portalType, credentials }),
  }));
}
export async function disconnectUyap(role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/connection`, { method: 'DELETE', headers: uyapHeaders(role, true) }));
}
export async function getDocumentSummary(caseId, docId, role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/cases/${caseId}/documents/${docId}/summary`, { headers: uyapHeaders(role) }));
}

export async function listCases(role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/cases`, { headers: uyapHeaders(role) }));
}
export async function getCaseDetail(id, role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/cases/${id}`, { headers: uyapHeaders(role) }));
}
export async function createCase(payload, role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/cases`, { method: 'POST', headers: uyapHeaders(role), body: JSON.stringify(payload) }));
}
export async function deleteCase(id, role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/cases/${id}`, { method: 'DELETE', headers: uyapHeaders(role, true) }));
}
export async function uploadCaseDocument(id, file, source, role) {
  const form = new FormData();
  form.append('file', file);
  if (source) form.append('source', source);
  const h = { 'x-eimza-grant': getEimzaGrant() };
  h['Authorization'] = getAuthHeader(role);
  if (role && !hasRealSession()) h['x-app-role'] = role;
  return uyapJson(await apiFetch(`${API}/api/uyap/cases/${id}/documents`, { method: 'POST', headers: h, body: form }));
}
export async function deleteCaseDocument(id, docId, role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/cases/${id}/documents/${docId}`, { method: 'DELETE', headers: uyapHeaders(role, true) }));
}
export function caseDocumentDownloadUrl(id, docId) {
  return `${API}/api/uyap/cases/${id}/documents/${docId}/download?grant=${encodeURIComponent(getEimzaGrant())}`;
}
export async function addCaseProgress(id, payload, role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/cases/${id}/progress`, { method: 'POST', headers: uyapHeaders(role, true), body: JSON.stringify(payload) }));
}
export async function syncCase(id, role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/cases/${id}/sync`, { method: 'POST', headers: uyapHeaders(role, true), body: '{}' }));
}
export async function askCase(id, question, role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/cases/${id}/ask`, { method: 'POST', headers: uyapHeaders(role), body: JSON.stringify({ question }) }));
}
export async function categorizeCase(id, role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/cases/${id}/categorize`, { method: 'POST', headers: uyapHeaders(role, true), body: '{}' }));
}
export async function listCaseCategories(role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/categories`, { headers: uyapHeaders(role) }));
}
// Citizen ("Vatandaş") portal — read-only, scoped to the signed-in person's TCKN.
export async function listCitizenCases(role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/citizen/cases`, { headers: uyapHeaders(role, true) }));
}
export async function getCitizenCaseDetail(id, role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/citizen/cases/${id}`, { headers: uyapHeaders(role, true) }));
}
export function citizenCaseDocumentDownloadUrl(id, docId) {
  return `${API}/api/uyap/citizen/cases/${id}/documents/${docId}/download?grant=${encodeURIComponent(getEimzaGrant())}`;
}
export async function askCitizenCase(id, question, role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/citizen/cases/${id}/ask`, { method: 'POST', headers: uyapHeaders(role, true), body: JSON.stringify({ question }) }));
}
export async function listNotifications(role, unreadOnly) {
  return uyapJson(await apiFetch(`${API}/api/uyap/notifications${unreadOnly ? '?unread=1' : ''}`, { headers: uyapHeaders(role) }));
}
export async function markNotificationRead(id, role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/notifications/${id}/read`, { method: 'POST', headers: uyapHeaders(role), body: '{}' }));
}
export async function markAllNotificationsRead(role) {
  return uyapJson(await apiFetch(`${API}/api/uyap/notifications/read-all`, { method: 'POST', headers: uyapHeaders(role), body: '{}' }));
}

/**
 * Read one drafting job tick. Used by the chat document card, which polls its own job
 * rather than blocking a chat turn — a quick draft still takes minutes.
 */
export async function getDraftJob(jobId, role) {
  const res = await apiFetch(`${API}/api/drafting/job/${jobId}`, { headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

// ── Global brand settings ─────────────────────────────────────────────────────
/** Read the org-wide brand. Unauthenticated on purpose so the login page is branded. */
export async function getBrandSettings() {
  const res = await fetch(`${API}/api/config/brand`, { credentials: 'same-origin' });
  if (!res.ok) return null;
  const data = await res.json().catch(() => ({}));
  return data.settings || null;
}

/** Admin only: save the brand for every user. */
export async function saveBrandSettings(settings, role) {
  const res = await apiFetch(`${API}/api/admin/brand`, {
    method: 'PUT', headers: headers(role), body: JSON.stringify({ settings }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data.settings || null;
}

// ── Saved drafts (server-side, owner-scoped) ──────────────────────────────────
/** Save a generated document to the user's own library. Explicit — nothing auto-saves. */
export async function saveDraftToLibrary(title, document, meta, role) {
  const res = await apiFetch(`${API}/api/drafting/library`, {
    method: 'POST', headers: headers(role), body: JSON.stringify({ title, document, meta }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

export async function listSavedDrafts(role) {
  const res = await apiFetch(`${API}/api/drafting/library`, { headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data.drafts || [];
}

export async function getSavedDraft(id, role) {
  const res = await apiFetch(`${API}/api/drafting/library/${id}`, { headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data.draft || null;
}

export async function deleteSavedDraft(id, role) {
  const res = await apiFetch(`${API}/api/drafting/library/${id}`, { method: 'DELETE', headers: headers(role) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

/** What the server can do (e.g. web search), so the chat offers only talents that will run. */
export async function getChatCapabilities(role) {
  const res = await apiFetch(`${API}/api/chat/capabilities`, {
    headers: role ? headers(role) : { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}
